// =============================================================================
// VariantBulkPriceModal — apply bulk price math to one Size group
// =============================================================================

import { useState } from 'react';
import { Button, Modal, ModalFooter } from '@tkpod/ui';
import {
  adjustPrice,
  parseMoneyInput,
  type PriceBulkOp,
  type PriceBulkUnit,
} from '@tkpod/shared';

export interface VariantBulkPriceModalItem {
  id: string;
  title: string | null;
  price: string;
  cost: string | null;
}

export function VariantBulkPriceModal({
  open,
  onClose,
  sizeLabel,
  items,
  drafts,
  onApply,
}: {
  open: boolean;
  onClose: () => void;
  sizeLabel: string;
  items: VariantBulkPriceModalItem[];
  drafts: Record<string, string>;
  onApply: (next: Record<string, string>) => void;
}) {
  const [op, setOp] = useState<PriceBulkOp>('set');
  const [unit, setUnit] = useState<PriceBulkUnit>('amount');
  const [value, setValue] = useState('');
  const [error, setError] = useState<string | null>(null);

  const unitLocked = op === 'set' || op === 'margin';
  const effectiveUnit: PriceBulkUnit =
    op === 'set' ? 'amount' : op === 'margin' ? 'percent' : unit;

  const handleApply = () => {
    const parsed = parseMoneyInput(value);
    if (parsed == null) {
      setError('Enter a value for the bulk pricing operation.');
      return;
    }
    const next: Record<string, string> = {};
    for (const item of items) {
      const base = parseMoneyInput(drafts[item.id] ?? item.price) ?? 0;
      const cost = item.cost != null ? Number.parseFloat(item.cost) : null;
      const result = adjustPrice(base, op, effectiveUnit, parsed, cost);
      if (result == null) continue;
      next[item.id] = result.toFixed(2);
    }
    onApply(next);
    setError(null);
    onClose();
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={`Edit bulk — ${sizeLabel}`}
      description={`Apply one price operation to all ${items.length} color(s) in this size.`}
      size="md"
    >
      <div className="flex flex-col gap-3">
        <div className="flex flex-wrap items-end gap-2">
          <label className="flex flex-col gap-1 text-xs text-text-tertiary">
            Operation
            <select
              className="rounded-md border border-border bg-surface px-2 py-1 text-sm text-text-primary"
              value={op}
              onChange={(e) => setOp(e.target.value as PriceBulkOp)}
            >
              <option value="set">Set retail price to</option>
              <option value="increase">Increase retail price by</option>
              <option value="decrease">Decrease retail price by</option>
              <option value="margin">Set from cost + margin</option>
            </select>
          </label>
          <label className="flex flex-col gap-1 text-xs text-text-tertiary">
            Unit
            <select
              className="rounded-md border border-border bg-surface px-2 py-1 text-sm text-text-primary"
              value={effectiveUnit}
              disabled={unitLocked}
              onChange={(e) => setUnit(e.target.value as PriceBulkUnit)}
            >
              <option value="amount">$</option>
              <option value="percent">%</option>
            </select>
          </label>
          <label className="flex flex-col gap-1 text-xs text-text-tertiary">
            Value
            <input
              className="w-28 rounded-md border border-border bg-surface px-2 py-1 text-sm text-text-primary"
              value={value}
              placeholder={effectiveUnit === 'percent' ? 'e.g. 20' : '0.00'}
              onChange={(e) => setValue(e.target.value)}
            />
          </label>
        </div>
        {error ? <p className="text-xs text-danger">{error}</p> : null}
      </div>
      <ModalFooter>
        <Button variant="outline" onClick={onClose}>
          Cancel
        </Button>
        <Button onClick={handleApply}>Apply to size</Button>
      </ModalFooter>
    </Modal>
  );
}
